import { cachedRow, upsertRows, forgetRowReads } from './core';
import type { SeedRow, TableName } from './core';

// Server-paged lists for tables too large to load whole. Each filter set keeps the IDs
// of the rows it has loaded, in server order; the rows live in the core cache, so an
// edit shows in every list. Totals come from the same endpoint with ?count=1.
export interface PageState { loading: boolean; done: boolean; error: string }
interface Page<F> { filters: F; ids: string[]; done: boolean; loading: Promise<void> | null; error: string }

export function pagedTable<F extends { limit?: number }>(table: TableName, query: (filters: F) => URLSearchParams | null, pageSize = 50) {
  const pages = new Map<string, Page<F>>();
  const counts = new Map<string, { filters: F; value?: number; loading: Promise<void> | null }>();
  const keyOf = (filters: F) => JSON.stringify(Object.entries(filters as Record<string, unknown>)
    .filter(([, value]) => value != null && value !== '' && value !== false).sort(([a], [b]) => a.localeCompare(b)));
  const updated = () => window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: table }));
  async function read(params: URLSearchParams): Promise<unknown> {
    const response = await fetch(`/api/v1/ui/${table}?${params}`);
    if (!response.ok) throw new Error(`Cannot load ${table} (${response.status})`);
    return response.json();
  }

  // fresh=true reloads the first page and keeps rows further down that were already shown.
  function load(filters: F, fresh = false): Promise<void> {
    const key = keyOf(filters);
    let page = pages.get(key);
    if (!page) pages.set(key, page = { filters, ids: [], done: false, loading: null, error: '' });
    // A refresh during a load waits for it, or rows saved meanwhile would be missed.
    if (page.loading) return fresh ? page.loading.then(() => load(filters, true)) : page.loading;
    if (page.done && !fresh) return Promise.resolve();
    const current = page, limit = filters.limit || pageSize;
    const params = query(filters);
    params?.set('limit', String(limit));
    if (params && !fresh) params.set('offset', String(current.ids.length));
    current.loading = (async () => {
      try {
        const value = params ? await read(params) : [];
        if (!Array.isArray(value)) throw new Error(`Invalid ${table}`);
        const ids = upsertRows(table, value as SeedRow[]).map(row => row.id as string);
        const added = ids.filter(id => !current.ids.includes(id));
        if (fresh) current.ids = [...added, ...current.ids];
        else current.ids.push(...added);
        // A page with nothing new ends the list even if the server ignored the offset.
        if (ids.length < limit || (!fresh && !added.length)) current.done = true;
        current.error = '';
      } catch (error: any) {
        current.error = error.message;
        window.dispatchEvent(new CustomEvent('cf:data-error', { detail: error.message }));
      } finally { current.loading = null; updated(); }
    })();
    return current.loading;
  }
  function loadCount(filters: F, fresh = false): Promise<void> {
    const key = keyOf(filters);
    let entry = counts.get(key);
    if (!entry) counts.set(key, entry = { filters, loading: null });
    if (entry.loading) return fresh ? entry.loading.then(() => loadCount(filters, true)) : entry.loading;
    if (entry.value !== undefined && !fresh) return Promise.resolve();
    const current = entry, params = query(filters);
    params?.set('count', '1');
    current.loading = (async () => {
      try {
        const value: any = params ? await read(params) : { count: 0 };
        current.value = Number(value?.count) || 0;
      } catch (error: any) {
        window.dispatchEvent(new CustomEvent('cf:data-error', { detail: error.message }));
      } finally { current.loading = null; updated(); }
    })();
    return current.loading;
  }

  return {
    /** Loaded rows for these filters in server order; the first call starts loading. */
    list(filters: F): SeedRow[] {
      const page = pages.get(keyOf(filters));
      if (!page) { void load(filters); return []; }
      return page.ids.map(id => cachedRow(table, id)).filter((row): row is SeedRow => !!row);
    },
    load: (filters: F) => load(filters),
    more: (filters: F) => load(filters),
    state(filters: F): PageState {
      const page = pages.get(keyOf(filters));
      return { loading: !page || !!page.loading, done: !!page?.done, error: page?.error || '' };
    },
    /** The server total for these filters; undefined until it arrives. */
    count(filters: F): number | undefined {
      const entry = counts.get(keyOf(filters));
      if (!entry) void loadCount(filters);
      return entry?.value;
    },
    /** Reloads the first page of every list and every total on screen. */
    refresh(): Promise<unknown> {
      forgetRowReads(table);
      return Promise.all([...[...pages.values()].map(page => load(page.filters, true)), ...[...counts.values()].map(entry => loadCount(entry.filters, true))]);
    },
    /** Drops every list and total, e.g. after a bulk delete; screens load them again. */
    forget() { pages.clear(); counts.clear(); forgetRowReads(table); updated(); },
  };
}
